import LinearProgress from '@material-ui/core/LinearProgress';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import React from 'react';
import { divider, primary } from '../Styles/Colours';
import { BackArrow, CloseIcon } from './Icons';
import { WindowEventListener } from './WindowEventListener';
import { isUndefined } from 'lodash';

import styles from './FullScreenModal.module.scss';

export enum ScrollContainerMode {
    Narrow,
    FullWidth
}

export enum ModalAnimationStyle {
    OpenAndClose,
    OpenOnly,
    CloseOnly,
    None
}

const BorderLinearProgress = withStyles({
    root: {
        height: 8,
        backgroundColor: divider,
        width: '100%',
        position: 'fixed',
        zIndex: 1
    },
    bar: {
        backgroundColor: primary
    }
})(LinearProgress);

export const FullScreenModal: React.FC<{
    title: string | JSX.Element;
    info: string | JSX.Element;
    nonInteractivePlaceholder?: React.ReactNode;
    onClose(): void;
    onBack?(): void;
    hideBackButton?: boolean;
    progressPercent?: number;
    /**
     * Renders above `title`
     */
    alertContent?: React.ReactNode;
    /**
     * @deprecated In favour of `FullScreenModalButtonContainer`
     */
    buttons?: React.ReactNode;
    modalAnimationStyle?: ModalAnimationStyle;
    /**
     * Removes padding top from body container shifting content up. Mixes well with `alertContent`.
     */
    noPaddingTop?: boolean;
    /**
     * thinner body and title container width
     */
    slim?: boolean;
}> = props => {
    const containerClasses = classNames(styles.container, {
        [styles.animationFull]:
            props.modalAnimationStyle === undefined || props.modalAnimationStyle === ModalAnimationStyle.OpenAndClose,
        [styles.animationOpenOnly]: props.modalAnimationStyle === ModalAnimationStyle.OpenOnly,
        [styles.animationCloseOnly]: props.modalAnimationStyle === ModalAnimationStyle.CloseOnly
    });

    return (
        <WindowEventListener
            onKeyDown={e => {
                if (props.nonInteractivePlaceholder) return;
                if (e.key === 'Escape') {
                    props.onClose();
                }
            }}
        >
            <div className={containerClasses}>
                {!props.nonInteractivePlaceholder && !isUndefined(props.progressPercent) && (
                    <BorderLinearProgress variant='determinate' color='secondary' value={props.progressPercent} />
                )}
                {!props.hideBackButton && !props.nonInteractivePlaceholder && props.onBack && (
                    <div className={styles.backButton} onClick={props.onBack} data-test-id={`navigation-control-back`}>
                        <BackArrow />
                    </div>
                )}
                {!props.nonInteractivePlaceholder && (
                    <div
                        className={styles.closeButton}
                        onClick={props.onClose}
                        data-test-id={`navigation-control-close`}
                    >
                        <CloseIcon />
                    </div>
                )}
                <div
                    className={classNames(styles.body, {
                        [styles.bodyPaddingTopNone]: props.noPaddingTop,
                        [styles.slim]: props.slim
                    })}
                >
                    {props.alertContent}
                    {props.nonInteractivePlaceholder ?? (
                        <>
                            <div className={styles.title}>{props.title}</div>
                            {props.info && <div className={styles.info}>{props.info}</div>}
                            {props.children}
                            {props.buttons && (
                                <FullScreenModalButtonContainer>{props.buttons}</FullScreenModalButtonContainer>
                            )}
                        </>
                    )}
                </div>
            </div>
        </WindowEventListener>
    );
};

/**
 * @description Button container for FullScreenModal. Useful for positioning your buttons anywhere inside the modal.
 */
export const FullScreenModalButtonContainer: React.FC = ({ children }) => (
    <div className={styles.buttonContainer}>{children}</div>
);

export const FullScreenModalScrollContainer: React.FC<{
    mode?: ScrollContainerMode;
}> = props => {
    const mode = props.mode ?? ScrollContainerMode.Narrow;
    const containerClasses = classNames(styles.scrollContainer, {
        [styles.narrow]: mode === ScrollContainerMode.Narrow,
        [styles.fullWidth]: mode === ScrollContainerMode.FullWidth
    });

    return <div className={containerClasses}>{props.children}</div>;
};
